/**
 * frescor — o critério ÚNICO de ausência, vencimento e impossibilidade de um
 * dado, em módulo PURO e compartilhado entre servidor e navegador.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE ARQUIVO EXISTE, E POR QUE EM `packages/shared`
 *
 * Ele é a extração de `apps/web/src/components/substation/aggregates.ts`, cujo
 * cabeçalho registra DEZOITO defeitos da mesma família: "o agregado trata a
 * ausência, a IDADE ou a IMPOSSIBILIDADE de um dado como se fosse dado válido".
 * O diagnóstico de lá vale literalmente aqui:
 *
 *   "enquanto cada agregado tinha a sua própria varredura de `points`, o
 *    critério de elegibilidade era reescrito por extenso em cada ponto do
 *    código, e bastava esquecer metade dele."
 *
 * ---------------------------------------------------------------------------
 * DÍVIDA ABERTA, E ELA CONTRADIZ O PARÁGRAFO ACIMA — leia antes de confiar.
 *
 * `apps/web/src/components/substation/aggregates.ts` **ainda tem a sua própria
 * cópia** de `estaVencido`, `temCarimboFuturo`, `temLeituraCorrente`,
 * `separarPorFrescor`, `estadoDeLeitura`, `cobertura`, `discriminarPorCausa`,
 * `qualificadorDeCobertura`, `TOLERANCIA_DE_RELOGIO_SEGUNDOS` e
 * `BaseDeAgregados`, e não importa nada daqui. A spec dizia "migram"; hoje são
 * DUAS implementações, que é o mecanismo do problema e não o remédio.
 *
 * A redução daquele arquivo a re-export é do frontend (o arquivo é dele), está
 * atribuída e pendente. Até ela entrar, este módulo é a fonte de verdade da FROTA
 * e a Subestação segue na cópia local — e a divergência que já existe entre as
 * duas está registrada no comentário de `semGrandeza`, em `discriminarPorCausa`.
 * Quem for consertar outra coisa aqui tem de olhar lá.
 *
 * A frota tem o mesmo problema com uma agravante: o CCO (`cco-routes.ts`) e
 * `/api/telemetria-frota` agregam no SERVIDOR, enquanto o Mapa e a tabela de
 * Dados dos Ônibus agregam no CLIENTE. Um módulo por lado seriam DUAS
 * implementações de um critério — que é o mecanismo do problema, não o remédio.
 * Por isso o módulo mora em `shared` e não numa pasta do front.
 *
 * Os comentários de justificativa vieram JUNTO. Eles são o registro dos 18
 * casos: não são enfeite, são a razão de cada linha estar escrita assim e a
 * única defesa contra alguém "simplificar" de volta para o defeito.
 *
 * ---------------------------------------------------------------------------
 * DOIS EIXOS, NÃO UM — o que esta extração acrescenta
 *
 * `aggregates.ts` modela **estado da leitura**: `sem-leitura` | `vencida` |
 * `futura` | `corrente`. A frota precisa de um segundo eixo, ORTOGONAL:
 * **suporte do campo pela fonte**. Não é um quinto estado da leitura — é
 * propriedade do par (fonte, campo), e vale para os 16 veículos ao mesmo tempo.
 *
 * |                        | fonte fornece                    | fonte NÃO fornece          |
 * |------------------------|----------------------------------|----------------------------|
 * | sem leitura            | "sem leitura para este veículo"  | "não fornecido por <fonte>" |
 * | age > limiar           | valor + "(último)" + degradação  | "não fornecido por <fonte>" |
 * | age < -5               | "carimbo à frente do relógio"    | "não fornecido por <fonte>" |
 * | corrente, campo NULL   | "leitura sem <grandeza>"         | "não fornecido por <fonte>" |
 * | corrente, campo com    | o valor                          | **impossível — bug de dado**|
 *
 * A coluna da direita COLAPSA, e é essa economia que justifica o eixo separado:
 * 16 veículos × 4 estados não precisam de 64 frases. E a última célula importa:
 * campo listado em `unsupported_fields` chegando COM VALOR é inconsistência de
 * fonte, e tem de aparecer na tela — ver `valorImpossivel`.
 *
 * ---------------------------------------------------------------------------
 * O QUE A EXAUSTIVIDADE DOS `Record` NÃO COBRE — leia antes de acrescentar um
 * estado.
 *
 * Os `Record` cobrem APRESENTAÇÃO. A CONTABILIDADE decide por PREDICADO, não
 * por índice da união, e um estado novo passaria silencioso por ela:
 * `temLeituraCorrente` nega predicados específicos, então um estado novo seria
 * tratado como ELEGÍVEL por omissão — exatamente o modo de falha do décimo caso.
 * Quem acrescenta um estado percorre os predicados à mão; o compilador cobra os
 * `Record` e cala sobre a elegibilidade.
 */
/**
 * Adapta "idade ou nada" para a forma `latest`.
 *
 * `fleet_snapshot.age_seconds` vem `NULL` quando `updated_at IS NULL` — a view
 * é explícita sobre isso porque AUSÊNCIA NÃO É IDADE. Esta função é a fronteira
 * onde esse `NULL` vira `latest: null` em vez de virar `ageSeconds: 0`, que
 * seria o clamp de novo, com outro nome.
 */
export function leituraDeIdade(ageSeconds) {
    return ageSeconds === null || ageSeconds === undefined ? null : { ageSeconds };
}
/**
 * Limiar ÚNICO de frescor da frota, servido no envelope como
 * `configuration.staleAfterSeconds`.
 *
 * Havia QUATRO limiares diferentes classificando a MESMA frota, e três deles
 * DESCARTAVAM o veículo vencido antes de contá-lo:
 *
 *   /api/telemetria-frota (`is_active`)  120 s  frescor
 *   MapaFrotaPage.isStale (cliente)      120 s  frescor, contra o relógio do NAVEGADOR
 *   SQL de /api/telemetria-frota/mapa     600 s  corte de LINHA, não frescor
 *   manobrista-routes.ts:199              900 s  candidatura à fila do pátio
 *
 * Com 10 de 16 veículos parados na origem, cada tela dizia uma coisa diferente
 * sobre os mesmos veículos. Os quatro convergem aqui, com UMA exceção nomeada
 * (abaixo) — porque ela mede outra coisa, não o mesmo com outro número.
 */
export const FLEET_STALE_AFTER_SECONDS = 120;
/**
 * A EXCEÇÃO NOMEADA — e ela não é frescor.
 *
 * Os 900 s do Manobrista respondem "este veículo é CANDIDATO à fila do pátio?",
 * que é pergunta diferente de "esta leitura é corrente?". Unificar as duas em
 * `FLEET_STALE_AFTER_SECONDS` seria fundir dois conceitos por coincidência de
 * unidade, que é a mesma classe de erro que fez `active` significar três coisas
 * neste banco.
 *
 * A CONSEQUÊNCIA É OBRIGATÓRIA, não opcional: um veículo de 8 minutos é
 * candidato à fila **E** stale, e a linha tem de dizer AS DUAS COISAS. Hoje o
 * corte de 900 s está dentro da SQL (`fleet_pos`), então 10 dos 16 veículos
 * desaparecem antes do `dist_km` e a fila é apresentada como completa. **Não
 * saber onde 10 ônibus estão é informação de primeira ordem, não uma linha a
 * omitir.** E `inside = dist_km <= radiusKm` sobre GPS de 14 minutos
 * apresentado como "está no pátio" viola o CLAUDE.md nestas palavras: *posição
 * conhecida não é posição atual*.
 */
export const MANOBRISTA_POSITION_WINDOW_SECONDS = 900;
/**
 * TOLERÂNCIA DE RELÓGIO — 5 segundos, e o número é escolha justificada.
 *
 * O servidor usa `FLOOR`, deliberadamente: um carimbo 1 ms adiantado sai `-1`,
 * nunca `0`. O sinal fica crisp — QUALQUER futuro é estritamente negativo. Isso
 * é certo no contrato e errado na tela: desvio subsegundo entre o relógio do
 * coletor e o do servidor é ruído normal de NTP, e sem banda a tela gritaria
 * "carimbo à frente do relógio" por 1 ms de jitter.
 *
 * POR QUE 5 s, e não 0 nem 60:
 *
 *  - abaixo de ~1 s está o jitter de NTP e de rede, e a deriva de relógio de VM
 *    entre dois hosts sincronizados. 5 s absorve isso com folga.
 *  - as falhas que importam estão ORDENS DE GRANDEZA acima: fuso horário errado
 *    são 3.600 s ou 10.800 s; data errada são 86.400 s; RTC de gateway sem
 *    sincronismo deriva para minutos. Não há falha plausível aterrissando entre
 *    5 s e 60 s.
 *  - uma banda generosa (60 s) engoliria deriva real de RTC, que é justamente o
 *    que precisa aparecer enquanto ainda dá para agir.
 *
 * PRESSUPOSTO QUE A FROTA RESOLVEU — e este parágrafo existia como advertência
 * em `aggregates.ts`, antes de ter resposta. Os 5 s valem para carimbo e
 * relógio de comparação NO MESMO HOST. Com o `datalbus_sync.sql` gravando
 * `updated_at` com o relógio do POSTGRES e a idade sendo computada no host do
 * NODE, o skew entre dois hosts é tipicamente de POUCOS SEGUNDOS — cai em cima
 * desta banda, e um skew permanente de 6 a 8 s acenderia "carimbo à frente" em
 * TODOS os veículos, para sempre.
 *
 * A saída, que a advertência já nomeava — "não é alargar o número: é o servidor
 * computar a idade contra o mesmo relógio que gravou o carimbo" — está
 * IMPLEMENTADA: `public.fleet_snapshot.age_seconds` é
 * `FLOOR(EXTRACT(EPOCH FROM (now() - t.updated_at)))::int`, com o `now()` do
 * Postgres. Resolvido por construção. **Se alguém mover essa conta para o Node,
 * este número deixa de valer** — e é por isso que a migration 0026 registra a
 * proibição na própria view.
 *
 * A BANDA VIVE NA APRESENTAÇÃO E NUNCA ZERA O CAMPO. `ageSeconds` continua
 * negativo no dado, a idade continua sendo impressa com sinal, e a tabela
 * continua mostrando o carimbo cru. O que a banda governa é o ALARME — a pill,
 * a degradação da célula e a saída dos agregados —, não a verdade do número.
 * Zerar o campo aqui seria o terceiro clamp da fila, com outro nome.
 */
export const TOLERANCIA_DE_RELOGIO_SEGUNDOS = 5;
/* ---------------------------------------------------------------------------
 * OS PREDICADOS. O CRITÉRIO É UM.
 * ------------------------------------------------------------------------- */
/**
 * Item VENCIDO: respondeu no passado e a resposta envelheceu.
 *
 * `latest === null` NÃO é vencido, e a distinção é deliberada: "não existe
 * leitura" é um estado diferente de "a leitura é velha". O primeiro é dito com
 * "sem leitura para este veículo"; o segundo degrada o valor que existe.
 * Tratar os dois como o mesmo faria a tela inteira vestir o chrome de "sem
 * sinal" numa frota que simplesmente ainda não coletou nada.
 */
export function estaVencido(item, cfg) {
    if (cfg.simulated)
        return false;
    return item.latest != null && item.latest.ageSeconds > cfg.staleAfterSeconds;
}
/**
 * Carimbo de tempo À FRENTE do relógio do servidor — o QUARTO ESTADO de uma
 * leitura.
 *
 * Os três anteriores eram sobre a leitura existir e ter idade aceitável. Este é
 * sobre ela ser SITUÁVEL NO TEMPO: a leitura está presente, traz a grandeza, e é
 * temporalmente impossível. Nenhum dos predicados anteriores pergunta isso —
 * `estaVencido` pergunta `age > limiar`, e `-10800 > 120` é falso, então uma
 * leitura de três horas no futuro passava como a mais fresca possível.
 *
 * Dois clamps em fila apagavam a evidência antes de qualquer predicado olhar, e
 * o efeito medido na tela foi o card dizendo "última leitura há 0 s" com a
 * tabela, quatro dobras abaixo, imprimindo um horário três horas no futuro na
 * mesma linha — e 59,55 Hz, faixa de ATENÇÃO, publicado como leitura corrente
 * por uma fonte que não sabe que horas são.
 *
 * Alcançável por meios banais, e a frota tem o seu: o `datalbus_sync.sql` copia
 * de `vgpl.fleet_live` por `mysql_fdw` com `AT TIME ZONE 'UTC'` MANUAL. Um erro
 * de fuso ali produz 10.800 s de carimbo futuro em 16 veículos DE UMA VEZ — e
 * hoje as seis telas de frota diriam "há 0 s". E a direção agrava: carimbo
 * futuro SE AUTOCURA com o tempo, some antes de alguém reproduzir.
 */
export function temCarimboFuturo(item, cfg) {
    if (cfg.simulated)
        return false;
    return item.latest != null && item.latest.ageSeconds < -TOLERANCIA_DE_RELOGIO_SEGUNDOS;
}
/**
 * Item com leitura DE VERDADE e corrente — o décimo caso da família.
 *
 * `!estaVencido(x)` NÃO é isto, e a diferença derrubou a tela num cenário que
 * ninguém tinha imaginado: um ponto cadastrado que nunca reportou
 * (`latest: null`) não está vencido, então entrava em `frescos` — e como o
 * gatilho do fallback era `frescos.length > 0`, a base nunca caía para a lista
 * completa. A base virava um único item SEM LEITURA NENHUMA, e a tela dizia
 * "Nenhum ponto com leitura" enquanto três cards imprimiam valores.
 *
 * `estaVencido` continua devolvendo `false` para `latest === null`, e continua
 * certo: ausência não é vencimento. O que estava errado era usar a NEGAÇÃO dele
 * como prova de que existe leitura. São duas perguntas diferentes, e agora têm
 * dois nomes.
 */
export function temLeituraCorrente(item, cfg) {
    return item.latest !== null && !estaVencido(item, cfg) && !temCarimboFuturo(item, cfg);
}
/**
 * Estado de UM item, pelo critério único. É a função que toda célula, pin e
 * linha chama — nenhuma reaplica regra, e nenhuma pode divergir da outra sobre
 * o mesmo item.
 *
 * Sobre a ORDEM dos testes: `futura` vem antes de `vencida` por clareza de
 * leitura, NÃO por necessidade — os dois predicados são mutuamente exclusivos
 * (`age < -5` e `age > 120` não se sobrepõem), e inverter a ordem não muda
 * resultado nenhum. Em `aggregates.ts` estava escrito que a ordem "importava" e
 * a mutação provou que não: o teste que deveria pegar a inversão passou verde.
 * Alegação de rigor que o teste não sustenta é pior que nenhuma alegação.
 */
export function estadoDeLeitura(item, cfg) {
    if (cfg.simulated)
        return 'neutra';
    if (item.latest === null)
        return 'sem-leitura';
    if (temCarimboFuturo(item, cfg))
        return 'futura';
    if (estaVencido(item, cfg))
        return 'vencida';
    return 'corrente';
}
/** Separa os itens, uma vez, para todo mundo. */
export function separarPorFrescor(itens, cfg) {
    return {
        frescos: itens.filter((x) => !estaVencido(x, cfg) && !temCarimboFuturo(x, cfg)),
        vencidos: itens.filter((x) => estaVencido(x, cfg)),
        futuros: itens.filter((x) => temCarimboFuturo(x, cfg)),
        correntes: itens.filter((x) => temLeituraCorrente(x, cfg)),
    };
}
/* ---------------------------------------------------------------------------
 * O SEGUNDO EIXO: SUPORTE DO CAMPO PELA FONTE
 * ------------------------------------------------------------------------- */
/**
 * A fonte deste item é INCAPAZ de fornecer este campo?
 *
 * Único lugar que interpreta `unsupported_fields`. Uma segunda ocorrência da
 * string `'unsupported_fields'` fora das três views da migration 0026 e deste
 * módulo é a regressão — e um `grep` de uma linha a encontra.
 *
 * Isto é propriedade do par (fonte, campo), não do item: vale para os 16
 * veículos ao mesmo tempo, e é por isso que a coluna direita da matriz colapsa.
 */
export function fonteNaoFornece(unsupportedFields, campo) {
    return unsupportedFields != null && unsupportedFields.includes(campo);
}
/**
 * A ÚLTIMA CÉLULA DA MATRIZ: campo declarado impossível chegando COM VALOR.
 *
 * Se `unsupported_fields` diz `charging_power_kw` impossível e o banco trouxer
 * um número, a contradição TEM DE APARECER — não ser engolida. É invariante
 * testável e é o item 10 da lista de sanity operacional.
 *
 * Duas causas reais, e as duas importam para quem opera: a lista está errada
 * (fonte passou a fornecer o campo e ninguém atualizou `telemetry_sources`), ou
 * o dado veio de outra fonte por algum caminho que não deveria existir. Nos
 * dois casos, engolir o valor esconde o defeito e mantém a tela mentindo.
 *
 * NÃO devolve o valor "corrigido" de propósito: quem chama decide se renderiza
 * a contradição ou registra alerta, e nenhuma das duas saídas é apagar o
 * número.
 */
export function valorImpossivel(unsupportedFields, campo, valor) {
    return fonteNaoFornece(unsupportedFields, campo) && valor !== null && valor !== undefined;
}
/**
 * O classificador, sobre DOIS NÚMEROS — serve para qualquer conjunto.
 *
 * Nasceu para as entradas de MT e vale igual para transformadores e para
 * veículos: a décima quinta ocorrência da família foi um ramo testar
 * `somados === 1` ANTES de comparar com o total, e curto-circuitar para
 * "Somente Transformador 1" com 1 de 3 contribuindo. Um classificador, N
 * vocabulários: cada conjunto tem o seu `Record`, e todos herdam a
 * exaustividade.
 *
 * É este classificador que fecha o `soc_medio` da frota: `AVG` sobre todas as
 * linhas, sem denominador, com SOC presente em 14 de 16 veículos, imprime "SOC
 * médio" como se fosse da frota. **Agregado que soma M de N tem de declarar M
 * de N**, e o denominador é a TOPOLOGIA VISÍVEL (16), nunca quem respondeu.
 *
 * DEPENDÊNCIA DE ORDEM, registrada porque é entre dois classificadores e não
 * garantia local: com `total === 0` isto devolve `'nenhuma'`, cuja frase
 * implicaria existir item mudo. Consumidor que possa receber conjunto vazio
 * tem de tratar `total === 0` antes de chegar aqui.
 */
export function cobertura(contribuintes, total) {
    if (contribuintes === 0)
        return 'nenhuma';
    if (contribuintes >= total)
        return (total === 1 ? 'unica' : 'todas');
    return contribuintes === 1 ? 'uma-de-varias' : 'parcial';
}
/**
 * Classifica cada excluído pela causa, pelo MESMO `estadoDeLeitura` que a
 * célula usa — em vez de baldes escritos à mão.
 *
 * A versão anterior tinha três baldes manuais e NENHUM para carimbo à frente: o
 * item futuro entrava em `ignorados` e não casava causa nenhuma, então a
 * palavra do único balde preenchido ia para o conjunto — o resumo dizia "2 de 3
 * sem leitura corrente" com um dos dois marcado, ao lado, como problema de
 * relógio. Derivar do classificador fecha a divergência por construção.
 *
 * `naoSuportado` TEM PRECEDÊNCIA sobre os quatro estados de leitura, e não é
 * arbitrário: impossibilidade é propriedade do par (fonte, campo) e NÃO DEPENDE
 * do estado da leitura. Um veículo Datalbus parado há 4 semanas continua sem
 * fornecer `range_km` — dizer "vencido" sobre ele mandaria o operador esperar
 * um dado que não vem nem com sinal fresco. É a coluna direita da matriz
 * colapsando, que é a economia que justifica o eixo separado.
 *
 * `suporte` é OPCIONAL e o default é "a fonte fornece tudo": os chamadores da
 * Subestação (um único medidor, sem eixo de fonte) compilam sem alteração.
 */
export function discriminarPorCausa(conjunto, contribuintes, 
/* `NonNullable<T['latest']>`, e não um segundo parâmetro de tipo `L`: com
   `<L, T extends ComLeitura<L>>` o compilador infere `L` do CONSTRAINT antes de
   olhar o argumento, resolve `L = LeituraDatada`, e rejeita qualquer seletor
   que leia um campo concreto da leitura. Derivando do próprio `T`, a inferência
   é de uma só direção e funciona para `SubstationPoint` e para a linha de
   `fleet_snapshot` sem anotação no ponto de chamada. */
grandeza, cfg, suporte = () => true) {
    const dentro = new Set(contribuintes.map((x) => x.key));
    const todosFora = conjunto.filter((x) => !dentro.has(x.key));
    const naoSuportado = todosFora.filter((x) => !suporte(x));
    const fora = todosFora.filter((x) => suporte(x));
    const porEstado = (alvo) => fora.filter((x) => estadoDeLeitura(x, cfg) === alvo).length;
    return {
        semLeitura: porEstado('sem-leitura'),
        vencidos: porEstado('vencida'),
        carimboFuturo: porEstado('futura'),
        /* Corrente e fora da conta só pode ser falta da COLUNA.
           ---------------------------------------------------------------------
           MUDANÇA SEMÂNTICA EM RELAÇÃO AO ORIGINAL, registrada porque a spec dizia
           "migram sem alteração semântica" e esta é a exceção.
           O `aggregates.ts` da Subestação contava apenas `estadoDeLeitura === 'corrente'`;
           aqui entra também `'neutra'`, que é o estado do modo DEMONSTRAÇÃO. Razão: em
           simulado, `estadoDeLeitura` devolve `'neutra'` para TODO ponto, então o balde
           `semGrandeza` ficava sempre 0 e o item com leitura oca não casava causa
           nenhuma — caía em `ignorados` sem palavra, que é exatamente o defeito do
           carimbo à frente antes de ele ganhar balde próprio.
           CONSEQUÊNCIA A CONHECER: quando a Subestação passar a importar este módulo
           (hoje ela ainda tem a cópia local), o comportamento dela em modo
           demonstração MUDA — o qualificador passa a poder dizer "com <grandeza>" onde
           antes recuava para "consideradas". É melhora, não regressão, mas é mudança
           que ninguém pediu e tem de ser vista na tela antes de valer.
           --------------------------------------------------------------------- */
        semGrandeza: fora.filter((x) => x.latest !== null &&
            (estadoDeLeitura(x, cfg) === 'corrente' || estadoDeLeitura(x, cfg) === 'neutra') &&
            grandeza(x.latest) == null).length,
        naoSuportado: naoSuportado.length,
    };
}
/**
 * A FRASE de cada causa, por balde. `Record` sobre `keyof ExcluidosPorCausa` de
 * propósito, e não uma lista de ternários escrita à mão.
 *
 * Era uma lista de quatro ternários, e foi exatamente por isso que o quinto
 * balde precisou de decisão consciente: acrescentar `naoSuportado` à interface
 * NÃO quebrava a lista, então a impossibilidade entraria na contabilidade e
 * ficaria MUDA na frase — que é o modo de falha que este módulo inteiro
 * combate. Com `Record`, um sexto balde não compila até ser escrito aqui.
 *
 * A ORDEM DE PRECEDÊNCIA é a ordem de declaração das chaves (objeto literal com
 * chaves de string preserva ordem de inserção). Ela vai da causa mais
 * informativa à menos: impossibilidade da fonte primeiro, porque é a única que
 * diz "não espere".
 */
const FRASE_DA_CAUSA = {
    naoSuportado: (g) => `de fonte que fornece ${g}`,
    vencidos: () => 'com leitura corrente',
    semLeitura: () => 'com leitura',
    carimboFuturo: () => 'com carimbo de tempo consistente',
    semGrandeza: (g) => `com ${g}`,
};
/**
 * O QUALIFICADOR da cobertura — a décima oitava ocorrência da família.
 *
 * Nomeia o que os CONTABILIZADOS têm, derivado do que falta aos excluídos. Os
 * `Record` de frase fixavam o literal "com leitura corrente" sem receber a
 * causa, e com dois itens frescos e o segundo respondendo em 16 s com a coluna
 * nula, a tela dizia:
 *
 *     KPI     "Somente Ramal A · 1 de 2 entradas com leitura corrente"
 *     nó B    — · "em leitura" · LED verde
 *     card B  "Última leitura há 16 s"
 *
 * O KPI negando a leitura que o nó ao lado afirmava.
 *
 * E RECUA PARA O NEUTRO QUANDO AS CAUSAS DIVERGEM. Com causas misturadas,
 * nenhuma das cinco frases descreve os contabilizados sem mentir sobre parte
 * dos excluídos. "consideradas" é verdadeiro em qualquer combinação — é o mesmo
 * recuo do "fora da soma" do QGBT. **Precisão que mente é pior que
 * generalidade honesta.**
 */
export function qualificadorDeCobertura(excluidos, grandeza) {
    const causas = Object.keys(FRASE_DA_CAUSA)
        .filter((causa) => excluidos[causa] > 0)
        .map((causa) => FRASE_DA_CAUSA[causa](grandeza));
    return causas.length === 1 ? causas[0] : 'consideradas';
}
/**
 * Base dos agregados: só itens com leitura corrente.
 *
 * Quando NENHUM item está corrente, a base cai para todos — porque aí o número
 * volta a ser exibido, mas com o chrome de "sem sinal" por cima, que é o
 * tratamento correto para "este é o último snapshot". O que não pode acontecer é
 * o caso do meio: misturar item vivo com item mudo no mesmo agregado, sem dizer
 * nada.
 *
 * ESTE É O ÚNICO LUGAR que produz um `BaseDeAgregados`: há exatamente UMA
 * asserção `as BaseDeAgregados` em produção, na linha do `return` abaixo. O
 * número importa para quem for auditar — é a única fronteira em que o critério
 * vira garantia de tipo, e um `grep` de uma linha a encontra. Se aparecer uma
 * segunda, ela é a regressão.
 */
export function baseDeAgregados(itens, frescor) {
    /* Recebe o `Frescor` INTEIRO, e não o array do gatilho, de propósito: com
       dois arrays parecidos na mão (`frescos` e `correntes`), passar o errado é um
       descuido de uma palavra — e foi exatamente esse descuido que produziu o
       décimo caso. Assim não há o que escolher no ponto de chamada.
       O gatilho é `correntes`; a base continua sendo `frescos`, que inclui o item
       sem leitura: ele não contribui com nada e não precisa ser expulso. */
    return (frescor.correntes.length > 0 ? frescor.frescos : itens);
}
